import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TextField,
  Paper,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import {
  markdownTocToHtml,
  markdownToHtml,
} from '../../utils/markdownConverter';

interface TOCModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (newTocContent?: string) => void;
  tocContent: string;
  title: string;
}

// TOC Item interface
interface TOCItem {
  id: string;
  level: number; // 0 = title, 1 = main section, 2 = sub-section, 3 = sub-sub-section
  text: string;
  parentId?: string;
  order: number;
}

// View modes
type ViewMode = 'view' | 'edit' | 'markdown';

const TOCModal: React.FC<TOCModalProps> = ({
  open,
  onClose,
  onUpdate,
  tocContent,
  title,
}) => {
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [updating, setUpdating] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('view');

  // Parse TOC content when modal opens
  useEffect(() => {
    if (open) {
      parseTocContent(tocContent);
      setViewMode('view'); // Reset to view mode when opening
    }
  }, [open, tocContent]);

  // Generate unique ID
  const generateId = () =>
    `toc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Parse existing TOC markdown into structured format
  const parseTocContent = (content: string) => {
    const items: TOCItem[] = [];
    let orderCounter = 0;
    let mainSectionCounter = 0;
    const subSectionCounters: { [key: string]: number } = {};
    const subSubSectionCounters: { [key: string]: number } = {};

    if (!content.trim()) {
      // Create default title if no content
      items.push({
        id: generateId(),
        level: 0,
        text: 'Document Title',
        order: orderCounter++,
      });
      setTocItems(items);
      return;
    }

    const lines = content.split('\n').filter((line) => line.trim());

    for (const line of lines) {
      if (line.startsWith('# ')) {
        // Title
        items.push({
          id: generateId(),
          level: 0,
          text: line.substring(2).trim(),
          order: orderCounter++,
        });
      } else if (line.match(/^\d+\.\s+/)) {
        // Main section
        mainSectionCounter++;
        const text = line.replace(/^\d+\.\s+/, '').trim();
        const mainId = generateId();
        items.push({
          id: mainId,
          level: 1,
          text,
          order: orderCounter++,
        });
        subSectionCounters[mainId] = 0;
      } else if (line.match(/^\s*-\s+\d+\.\d+\s+/)) {
        // Sub-section (e.g., "  - 1.1 Text")
        const text = line.replace(/^\s*-\s+\d+\.\d+\s+/, '').trim();
        const lastMainItem = items.filter((item) => item.level === 1).pop();
        if (lastMainItem) {
          subSectionCounters[lastMainItem.id]++;
          const subId = generateId();
          items.push({
            id: subId,
            level: 2,
            text,
            parentId: lastMainItem.id,
            order: orderCounter++,
          });
          subSubSectionCounters[subId] = 0;
        }
      } else if (line.match(/^\s*-\s+\d+\.\d+\.\d+\s+/)) {
        // Sub-sub-section (e.g., "    - 1.1.1 Text")
        const text = line.replace(/^\s*-\s+\d+\.\d+\.\d+\s+/, '').trim();
        const lastSubItem = items.filter((item) => item.level === 2).pop();
        if (lastSubItem) {
          subSubSectionCounters[lastSubItem.id]++;
          items.push({
            id: generateId(),
            level: 3,
            text,
            parentId: lastSubItem.id,
            order: orderCounter++,
          });
        }
      }
    }

    setTocItems(items);
  };

  // Convert structured TOC back to markdown format
  const generateTocMarkdown = (items: TOCItem[]): string => {
    if (!items.length) return '';

    let markdown = '';
    let mainCounter = 0;
    const subCounters: { [parentId: string]: number } = {};
    const subSubCounters: { [parentId: string]: number } = {};

    for (const item of items.sort((a, b) => a.order - b.order)) {
      if (item.level === 0) {
        // Title
        markdown += `# ${item.text}\n\n`;
      } else if (item.level === 1) {
        // Main section
        mainCounter++;
        markdown += `${mainCounter}. ${item.text}\n`;
        subCounters[item.id] = 0;
      } else if (item.level === 2) {
        // Sub-section
        if (!item.parentId) continue;
        subCounters[item.parentId] = (subCounters[item.parentId] || 0) + 1;
        const parentMainIndex = items.filter(
          (i) => i.level === 1 && i.order < item.order
        ).length;
        markdown += `  - ${parentMainIndex}.${subCounters[item.parentId]} ${item.text}\n`;
        subSubCounters[item.id] = 0;
      } else if (item.level === 3) {
        // Sub-sub-section
        if (!item.parentId) continue;
        const subItem = items.find((i) => i.id === item.parentId);
        if (!subItem?.parentId) continue;

        subSubCounters[item.parentId] =
          (subSubCounters[item.parentId] || 0) + 1;
        const parentMainIndex = items.filter(
          (i) => i.level === 1 && i.order < subItem.order
        ).length;
        const parentSubIndex = subCounters[subItem.parentId];
        markdown += `    - ${parentMainIndex}.${parentSubIndex}.${subSubCounters[item.parentId]} ${item.text}\n`;
      }
    }

    return markdown.trim();
  };

  // Add new item
  const addItem = (level: number, parentId?: string) => {
    const newItem: TOCItem = {
      id: generateId(),
      level,
      text:
        level === 0
          ? 'New Title'
          : level === 1
            ? 'New Main Section'
            : level === 2
              ? 'New Sub-section'
              : 'New Sub-sub-section',
      parentId,
      order: Math.max(...tocItems.map((item) => item.order), -1) + 1,
    };

    setTocItems((prev) => [...prev, newItem]);
    setEditingId(newItem.id);
    setEditText(newItem.text);
  };

  // Start editing
  const startEdit = (item: TOCItem) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  // Save edit
  const saveEdit = () => {
    if (!editingId) return;

    setTocItems((prev) =>
      prev.map((item) =>
        item.id === editingId ? { ...item, text: editText.trim() } : item
      )
    );
    setEditingId(null);
    setEditText('');
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  // Delete item and its children
  const deleteItem = (itemId: string) => {
    const deleteItemAndChildren = (id: string): string[] => {
      const children = tocItems.filter((item) => item.parentId === id);
      let toDelete = [id];
      for (const child of children) {
        toDelete = [...toDelete, ...deleteItemAndChildren(child.id)];
      }
      return toDelete;
    };

    const idsToDelete = deleteItemAndChildren(itemId);
    setTocItems((prev) =>
      prev.filter((item) => !idsToDelete.includes(item.id))
    );
  };

  // Move item up/down
  const moveItem = (itemId: string, direction: 'up' | 'down') => {
    const item = tocItems.find((i) => i.id === itemId);
    if (!item) return;

    const siblingsAtSameLevel = tocItems
      .filter((i) => i.level === item.level && i.parentId === item.parentId)
      .sort((a, b) => a.order - b.order);

    const currentIndex = siblingsAtSameLevel.findIndex((i) => i.id === itemId);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (swapIndex < 0 || swapIndex >= siblingsAtSameLevel.length) return;

    const swapItem = siblingsAtSameLevel[swapIndex];

    setTocItems((prev) =>
      prev.map((i) => {
        if (i.id === item.id) return { ...i, order: swapItem.order };
        if (i.id === swapItem.id) return { ...i, order: item.order };
        return i;
      })
    );
  };

  // Get children of an item
  const getChildren = (parentId: string): TOCItem[] => {
    return tocItems
      .filter((item) => item.parentId === parentId)
      .sort((a, b) => a.order - b.order);
  };

  // Handle save TOC
  const handleSave = () => {
    setUpdating(true);
    const markdown = generateTocMarkdown(tocItems);
    onUpdate(markdown);
    setUpdating(false);
    onClose();
  };
  const renderTocHtml2 = (markdown: string) => {
    return { __html: markdownTocToHtml(markdown) };
  };

  // Handle view mode change
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // Cancel any ongoing edits when switching modes
      if (editingId) {
        setEditingId(null);
        setEditText('');
      }
    }
  };

  // Render TOC item
  const renderTocItem = (item: TOCItem, depth: number = 0) => {
    const isEditing = editingId === item.id;
    const children = getChildren(item.id);
    const canAddChildren = item.level < 3 && item.level > 1; // Max 3 levels deep
    const siblingsAtSameLevel = tocItems.filter(
      (i) => i.level === item.level && i.parentId === item.parentId
    );
    const itemIndex = siblingsAtSameLevel.findIndex((i) => i.id === item.id);

    return (
      <Box key={item.id} sx={{ mb: 1 }}>
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            ml: depth * 2,
            border:
              item.level === 0
                ? '2px solid #1976d2'
                : item.level === 1
                  ? '1px solid #1976d2'
                  : item.level === 2
                    ? '1px solid #666'
                    : '1px solid #999',
            backgroundColor:
              item.level === 0
                ? '#e3f2fd'
                : item.level === 1
                  ? '#f5f5f5'
                  : 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={
                item.level === 0
                  ? 'Title'
                  : item.level === 1
                    ? 'Main'
                    : item.level === 2
                      ? 'Sub'
                      : 'Sub-sub'
              }
              size="small"
              color={
                item.level === 0
                  ? 'primary'
                  : item.level === 1
                    ? 'secondary'
                    : item.level === 2
                      ? 'default'
                      : 'default'
              }
              variant="outlined"
            />

            {isEditing ? (
              <TextField
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit();
                  if (e.key === 'Escape') cancelEdit();
                }}
                autoFocus
                size="small"
                sx={{ flex: 1 }}
              />
            ) : (
              <Typography
                sx={{
                  flex: 1,
                  fontWeight: item.level <= 1 ? 'bold' : 'normal',
                }}
              >
                {item.text}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {isEditing ? (
                <>
                  <IconButton size="small" onClick={saveEdit} color="primary">
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={cancelEdit}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small" onClick={() => startEdit(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>

                  {itemIndex > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => moveItem(item.id, 'up')}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                  )}

                  {itemIndex < siblingsAtSameLevel.length - 1 && (
                    <IconButton
                      size="small"
                      onClick={() => moveItem(item.id, 'down')}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  )}

                  {canAddChildren && (
                    <IconButton
                      size="small"
                      onClick={() => addItem(item.level + 1, item.id)}
                      color="primary"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}

                  <IconButton
                    size="small"
                    onClick={() => deleteItem(item.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Render children */}
        {children.map((child) => renderTocItem(child, depth + 1))}
      </Box>
    );
  };

  // Get root items (title + main sections)
  const rootItems = tocItems
    .filter((item) => item.level <= 1 && !item.parentId)
    .sort((a, b) => a.order - b.order);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Table of Contents</Typography>

          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="view">
                <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                View
              </ToggleButton>
              <ToggleButton value="edit">
                <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                Edit
              </ToggleButton>
              <ToggleButton value="markdown">
                <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
                Markdown
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Add buttons only visible in edit mode */}
            {viewMode === 'edit' && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {tocItems.filter((item) => item.level === 0).length === 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addItem(0)}
                    size="small"
                  >
                    Add Title
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addItem(1)}
                  size="small"
                >
                  Add Main Section
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* View Mode - HTML formatted TOC */}
        {viewMode === 'view' && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Table of contents formatted for easy reading.
              </Typography>
            </Box>

            <Paper
              sx={{
                p: 2,
                maxHeight: '70vh',
                overflow: 'auto',
                bgcolor: 'background.paper',
              }}
            >
              <div
                className="markdown-toc-preview"
                dangerouslySetInnerHTML={renderTocHtml2(tocContent)}
                style={{ fontSize: '1rem', lineHeight: '1.6' }}
              />
            </Paper>
          </Box>
        )}

        {/* Edit Mode - Structured Editor */}
        {viewMode === 'edit' && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Use the structured editor below to build your table of contents.
              </Typography>
            </Box>

            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {rootItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No TOC items yet. Click "Add Title" or "Add Main Section" to
                    get started.
                  </Typography>
                </Box>
              ) : (
                rootItems.map((item) => renderTocItem(item))
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Preview of Generated Markdown:
              </Typography>
              <Paper
                sx={{
                  p: 1,
                  bgcolor: '#f5f5f5',
                  maxHeight: '150px',
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                >
                  {generateTocMarkdown(tocItems) || 'No content to preview'}
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Markdown Mode - Raw markdown view */}
        {viewMode === 'markdown' && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Raw markdown format of the table of contents.
              </Typography>
            </Box>

            <Paper
              sx={{
                p: 2,
                bgcolor: '#f5f5f5',
                maxHeight: '70vh',
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {tocContent || 'No content available'}
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {viewMode === 'edit' && tocItems.length > 0 ? 'Cancel' : 'Close'}
        </Button>

        {/* Save button only visible in edit mode */}
        {viewMode === 'edit' && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || tocItems.length === 0}
            startIcon={updating ? undefined : <SaveIcon />}
          >
            {updating ? 'Updating...' : 'Save TOC'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TOCModal;
